import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MessageSquareText, Activity } from "lucide-react";
import { V } from "@/lib/vocab";

/* Text capacity monitor — super-admin console (Communities page).
   The SMS twin of VoiceCapacityCard: outbound segments today vs the number pool's soft
   daily ceiling (~2,000 segments/day per A2P long code), 7-day volume and busiest community. */

const STATUS_STYLES = {
  ok: { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Healthy", bar: "bg-emerald-500" },
  watch: { chip: "bg-amber-50 text-amber-800 border-amber-200", label: "Watch", bar: "bg-amber-500" },
  high: { chip: "bg-red-50 text-red-700 border-red-200", label: "Near ceiling", bar: "bg-redline-500" },
};

export const TextCapacityCard = ({ onSummary }) => {
  const [cap, setCap] = useState(null);
  const summary = useRef(onSummary);
  summary.current = onSummary;

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/sms/capacity").then((r) => {
      if (!alive) return;
      setCap(r.data);
      summary.current?.({ status: r.data.status, label: (STATUS_STYLES[r.data.status] || STATUS_STYLES.ok).label, line: `${r.data.segments_today} segments today` });
    }).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  if (!cap) return null;
  const st = STATUS_STYLES[cap.status] || STATUS_STYLES.ok;
  const pct = cap.soft_daily_ceiling ? Math.min(100, Math.round((cap.segments_today / cap.soft_daily_ceiling) * 100)) : 0;

  return (
    <div data-testid="text-capacity-card" className="bg-white rounded-2xl border border-sand-200 px-5 py-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className={`h-9 w-9 rounded-xl flex items-center justify-center ${cap.last_hour > 0 ? "bg-emerald-50" : "bg-sand-100"}`}>
            <MessageSquareText className={`h-4.5 w-4.5 ${cap.last_hour > 0 ? "text-emerald-600" : "text-ink/40"}`} strokeWidth={1.75} />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Platform text throughput</span>
              <span data-testid="tc-status" className={`text-[10px] font-bold border rounded-full px-2 py-px ${st.chip}`}>{st.label}</span>
            </div>
            <div className="text-[10px] text-muted-foreground">All {V.lcs("org")} · numbers pooled (not per number)</div>
            <div className="text-sm mt-0.5">
              <span data-testid="tc-segments-today" className="font-heading font-extrabold text-lg text-ink">{cap.segments_today}</span>
              <span className="text-ink-500"> segments today · ceiling ~{cap.soft_daily_ceiling.toLocaleString()}/day</span>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-[160px] max-w-xs">
          <div className="h-1.5 rounded-full bg-sand-100 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${st.bar}`} style={{ width: `${Math.max(pct, cap.segments_today > 0 ? 4 : 0)}%` }} />
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Sent today: <span data-testid="tc-sent-today" className="font-semibold text-ink-600">{cap.sent_today}</span> · Received: <span data-testid="tc-received-today" className="font-semibold text-ink-600">{cap.received_today}</span> · Last hour: <span data-testid="tc-last-hour" className="font-semibold text-ink-600">{cap.last_hour}</span>
          </div>
        </div>
        <div className="flex items-center gap-5 text-sm">
          <div>
            <div className="text-[11px] text-muted-foreground">Messages · 7d</div>
            <div data-testid="tc-msgs-7d" className="font-heading font-extrabold text-ink">{cap.messages_7d}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground">Live numbers</div>
            <div data-testid="tc-active-numbers" className="font-heading font-extrabold text-ink">{cap.active_numbers}</div>
          </div>
          {cap.top_communities_7d?.[0] && (
            <div className="hidden lg:block">
              <div className="text-[11px] text-muted-foreground flex items-center gap-1" title="The community that sent and received the most texts in the last 7 days — who is using the shared text capacity"><Activity className="h-3 w-3" /> Heaviest texter · 7d</div>
              <div data-testid="tc-busiest" className="text-xs font-semibold text-ink-600 truncate max-w-[260px]" title={`${cap.top_communities_7d[0].name}: ${cap.top_communities_7d[0].messages} of ${cap.messages_7d} messages this week`}>
                {cap.top_communities_7d[0].name} <span className="text-muted-foreground font-medium">— {cap.top_communities_7d[0].messages} of {cap.messages_7d} msgs</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
